import React, { useMemo, useState, useEffect } from 'react';
import { useTable } from 'react-table';
import axios from 'axios';
import { useQuery, QueryClient, QueryClientProvider } from 'react-query';
import SearchBar from './SearchBar';
import Pagination from './Pagination';
import queryString from 'query-string';

const itemTypeToString = (itemTypeId) => {
  if (itemTypeId === 1) return 'Inventory Item'
  if (itemTypeId === 2) return 'Service'
  if (itemTypeId === 3) return 'Sales Tax'
  if (itemTypeId === 5) return 'Shipping/Freight'
}

const fetchData = (sorting, page, filters) => {
  const base = "/items.json"
  const filtersClone = {...filters, page: page + 1}
  if (Object.keys(sorting).length) filtersClone['sort_by'] = `${sorting.id} ${sorting.direction}`
  const stringifiedFilters = queryString.stringify(filtersClone);
  const qMark = stringifiedFilters.length ? '?' : ''

  // This does succeed at replacing the URL, but does break the forward/back buttons.
  // const url = '/items' + qMark + stringifiedFilters
  // history.replaceState(null, null, url);
  return axios.get(base + qMark + stringifiedFilters)
};
const fetchVendorData = () => axios.get(`/vendors.json`);

const TableQuery = () => {
  const localPage = parseInt(localStorage.getItem('page'));
  const localSortingStr = localStorage.getItem('sorting');
  const localSorting = localSortingStr ? JSON.parse(localSortingStr) : {};
  const localFiltersStr = localStorage.getItem('filters');
  const localFilters = localFiltersStr ? JSON.parse(localFiltersStr) : {};

  const [tableData, setTableData] = useState(null);
  const [meta, setMeta] = useState(null);
  const [vendorData, setVendorData] = useState(null);
  const [sorting, xSetSorting] = React.useState(localSorting)
  const [page, setPage] = React.useState(localPage || 0);
  const [filters, xSetFilters] = React.useState(localFilters)

  const setFilters = (filters) => {
    setPage(0)
    xSetFilters(filters)
  }

  const setSorting = (id) => {
    setPage(0)
    if (sorting.id === id) {
      if (sorting.direction === 'asc') {
        xSetSorting({ id, direction: 'desc' })
      } else {
        xSetSorting({})
      }
    } else {
      if (!id) {
        xSetSorting({})
      } else {
        xSetSorting({ id, direction: 'asc'})
      }
    }
  }

  useEffect(() => {
    localStorage.setItem('page', page)
    localStorage.setItem('sorting', JSON.stringify(sorting))
    localStorage.setItem('filters', JSON.stringify(filters))
  }, [page, sorting, filters])

  const { data: apiResponse, isLoading } = useQuery(['items', sorting, page, filters], () => fetchData(sorting, page, filters));
  const { data: vendorApiResponse, isLoading: isLoadingVendors } = useQuery('vendors', fetchVendorData);

  useEffect(() => {
    setTableData(apiResponse?.data?.list);
    setMeta(apiResponse?.data?.meta);
  }, [apiResponse])

  useEffect(() => {
    setVendorData(vendorApiResponse?.data);
  }, [vendorApiResponse])

  if (isLoading || isLoadingVendors || !tableData) {
    return <div>Loading...</div>
  }

  return (
    <TableInstance
      tableData={tableData}
      vendorData={vendorData}
      sorting={sorting}
      setSorting={setSorting}
      filters={filters}
      setFilters={setFilters}
      page={page}
      setPage={setPage}
      meta={meta}
    />
  );
}

const TableInstance = ({
  tableData,
  vendorData,
  sorting,
  setSorting,
  filters,
  setFilters,
  page,
  setPage,
  meta,
}) => {
  const currencyConfig = { style: 'currency', currency: 'USD' };

  const [columns, data] = useMemo(
    () => {
      const columns = [
        {
          Header: 'SKU',
          accessor: 'sku',
          id: 'LOWER(sku)',
        },
        {
          Header: 'Name',
          accessor: 'name',
          id: 'LOWER(name)',
        },
        {
          Header: 'Vendor',
          accessor: (i) => vendorData?.find(v => i.vendor_id === v.id)?.name || 'N/A',
          id: 'vendors.name'
        },
        {
          Header: 'Cost',
          accessor: (i) => Number.parseFloat(i.cost).toLocaleString('en-US', currencyConfig),
          id: 'cost',
        },
        {
          Header: 'Default Price',
          accessor: (i) => Number.parseFloat(i.default_price).toLocaleString('en-US', currencyConfig),
          id: 'default_price',
        },
        {
          Header: 'Item Type',
          accessor: (i) => itemTypeToString(i.item_type_id),
          id: 'item_types.name'
        },
        {
          Header: '',
          id: 'actions',
          accessor: (i) => (
            <span>
              <a
                href={`/items/${i.id}`}
                className='admin-btn-link'
                style={{ border: 'none', backgroundColor: "transparent", paddingRight: 7  }}
              >
                VIEW
              </a>
              <a
                href={`/items/${i.id}/edit`}
                className='admin-btn-link'
                style={{ border: 'none', backgroundColor: "transparent", paddingRight: 7 }}
              >
                EDIT
              </a>
              <a
                data-confirm="Are you sure?" 
                data-method="delete" 
                href={`/items/${i.id}`}
                className='admin-btn-link'
                style={{ border: 'none', backgroundColor: "transparent" }}
              >
                {!!i.disabled_at ? "RESTORE" : "DELETE"}
              </a>
            </span>
          )
        },
      ];
      return [columns, tableData];
    },
    [tableData]
  );

  const tableInstance = useTable({
    columns,
    data,
    // //...
    manualSorting: true,
    // onColumnFiltersChange: setColumnFilters, //hoist columnFilters state into our own state management
    onPaginationChange: setPage,
    onSortingChange: setSorting,
    setFilters,
    filters,
    page,
  })

  return (
    <TableLayout
      {...tableInstance}
      sorting={sorting}
      meta={meta}
      vendorData={vendorData}
    />
  );
}

const TableLayout = ({
  getTableProps,
  getTableBodyProps,
  headerGroups,
  rows,
  prepareRow,
  sorting,
  onSortingChange,
  onPaginationChange,
  page,
  filters,
  setFilters,
  meta,
  vendorData,
}) => {
  return (
    <div className='container'>
      <h1>Items &amp; Accessories</h1>
      <SearchBar
        filters={filters}
        setFilters={setFilters}
        sorting={sorting}
        onSortingChange={onSortingChange}
        vendorData={vendorData}
      />
      <Pagination
        currentPage={page}
        onPaginationChange={onPaginationChange}
        totalCount={meta.total}
        pageSize={meta.page_size}
      />
      <table {...getTableProps()} className="table table-condensed table-responsive admintable">
        <thead>
          {headerGroups.map(headerGroup => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map(column => (
                <th {...column.getHeaderProps()} onClick={() => onSortingChange(column.id)}>
                  <span style={{ marginRight: '20' }}>
                    {column.render('Header')}
                  </span>
                  {sorting?.id === column?.id && sorting.direction === 'asc' ? <span>&#9660;</span> : null}
                  {sorting?.id === column?.id && sorting.direction === 'desc' ? <span>&#9650;</span> : null}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {rows.map((row, i) => {
            prepareRow(row)
            return (
              <tr {...row.getRowProps()}>
                {row.cells.map(cell => {
                  return <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                })}
              </tr>
            )
          })}
        </tbody>
      </table>
      <Pagination
        currentPage={page}
        onPaginationChange={onPaginationChange}
        totalCount={meta.total}
        pageSize={meta.page_size}
      />
    </div>
  );
}

const client = new QueryClient();

const ReactQueryWithTable = () => {
  return (
    <QueryClientProvider client={client}>
      <TableQuery />
    </QueryClientProvider>
  );
}

export default ReactQueryWithTable;